import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';
import { storage } from '../utils/storage';

export const AuthContext = createContext();

export const AUTH_ACTIONS = {
  START: 'START',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LOADING: 'SET_LOADING'
};

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.START:
      return { ...state, isLoading: true, error: null };

    case AUTH_ACTIONS.SUCCESS:
      return { 
        ...state, 
        user: action.payload, 
        isAuthenticated: true, 
        isLoading: false, 
        error: null 
      };

    case AUTH_ACTIONS.FAILURE:
      return { 
        ...state, 
        user: null, 
        isAuthenticated: false, 
        isLoading: false, 
        error: action.payload 
      };

    case AUTH_ACTIONS.LOGOUT:
      return { 
        ...state, 
        user: null, 
        isAuthenticated: false, 
        isLoading: false, 
        error: null 
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return { 
        ...state, 
        user: { ...state.user, ...action.payload } 
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };

    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

    try {
      const cachedUser = storage.getUser();
      const token = localStorage.getItem('medigo_token');

      if (!token) {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        return;
      }

      const result = await authService.getCurrentUser();

      if (result.success && result.data) {
        dispatch({ type: AUTH_ACTIONS.SUCCESS, payload: result.data });
      } else if (cachedUser && authService.isAuthenticated()) {
        dispatch({ type: AUTH_ACTIONS.SUCCESS, payload: cachedUser });
      } else {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    } catch (error) {
      const cachedUser = storage.getUser();
      if (cachedUser && authService.isAuthenticated()) {
        dispatch({ type: AUTH_ACTIONS.SUCCESS, payload: cachedUser });
      } else {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    }
  };

  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.START });
    
    try {
      const result = await authService.login(credentials);

      if (result.success && result.data?.user) {
        dispatch({ type: AUTH_ACTIONS.SUCCESS, payload: result.data.user });
        return result;
      } else {
        dispatch({ type: AUTH_ACTIONS.FAILURE, payload: result.message });
        return result;
      }
    } catch (error) {
      const errorMessage = error.message || 'Login failed';
      dispatch({ type: AUTH_ACTIONS.FAILURE, payload: errorMessage });
      return {
        success: false,
        message: errorMessage
      };
    }
  };

  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.START });
    
    try {
      const result = await authService.register(userData);

      if (result.success && result.data?.user) {
        dispatch({ type: AUTH_ACTIONS.SUCCESS, payload: result.data.user });
        return result;
      } else {
        dispatch({ type: AUTH_ACTIONS.FAILURE, payload: result.message });
        return result;
      }
    } catch (error) {
      const errorMessage = error.message || 'Registration failed';
      dispatch({ type: AUTH_ACTIONS.FAILURE, payload: errorMessage });
      return {
        success: false,
        message: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Silent fail
    } finally {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  const forgotPassword = async (data) => {
    try {
      return await authService.forgotPassword(data);
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send reset instructions'
      };
    }
  };

  const verifyOTP = async (data) => {
    try {
      return await authService.verifyOTP(data);
    } catch (error) {
      return {
        success: false,
        message: error.message || 'OTP verification failed'
      };
    }
  };

  const resetPassword = async (data) => {
    try {
      return await authService.resetPassword(data);
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Password reset failed'
      };
    }
  };

  const verify = async (data) => {
    try {
      const result = await authService.verify(data);
      
      if (result.success && result.data) {
        dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: result.data });
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Verification failed'
      };
    }
  };

  const sendVerification = async (data) => {
    try {
      return await authService.sendVerification(data);
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Failed to send verification code'
      };
    }
  };

  const updateUser = (data) => {
    dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: data });
    authService.updateUserData(data);
  };

  const clearError = () => dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

  const hasRole = (role) => authService.hasRole(role);

  const hasAnyRole = (roles) => authService.hasAnyRole(roles);

  const isVerified = () => authService.isAccountVerified();

  const value = {
    ...state,
    login,
    register,
    logout,
    forgotPassword,
    verifyOTP,
    resetPassword,
    verify,
    sendVerification,
    updateUser,
    clearError,
    hasRole,
    hasAnyRole,
    isVerified
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};